import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import ErrorFallback from '@/layouts/ErrorFallback';
import * as Sentry from '@sentry/react';

import { Loader } from '@/components/ui/loader';

import { ProtectedRoute } from './routes';

{
  /* V2 ROUTES */
}
const BrandStrategy = lazy(() => import('@/V2/pages/brand-strategy'));
const PostIdeas = lazy(() => import('@/V2/pages/post-ideas'));
{
  /* V2 ROUTES */
}
const BrandWikiSetup = lazy(() => import('@/pages/BrandWikiSetup.tsx'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword.tsx'));
const SignUp = lazy(() => import('@/pages/SignUp'));
const SignIn = lazy(() => import('@/pages/SignIn'));
const Schedule = lazy(() => import('@/features/Schedule/Schedule'));
const BasePost = lazy(() => import('@/pages/BasePost'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Settings = lazy(() => import('@/pages/Settings'));
const DraftPosts = lazy(() => import('@/pages/DraftPosts.tsx'));
const Admin = lazy(() => import('@/pages/Admin'));
const AdminUsers = lazy(() => import('@/features/AdminDashboard/AdminUsers'));
const AdminLlmLogs = lazy(
  () => import('@/features/AdminDashboard/AdminLlmLogs')
);
const FormattedPosts = lazy(
  () => import('@/features/Post/FormattedPost/FormattedPost')
);
const MainLayout = lazy(() => import('@/layouts/MainLayout'));
const CabinetLayout = lazy(() => import('@/layouts/CabinetLayout'));
const SubscriptionSuccess = lazy(
  () => import('@/features/Stripe/SubscriptionSuccess')
);
const SubscriptionFailed = lazy(
  () => import('@/features/Stripe/SubscriptionFailed')
);
const Clients = lazy(() => import('@/pages/Clients'));
const BrandSettings = lazy(() => import('@/pages/BrandSettings'));
const StartOnboarding = lazy(() => import('@/pages/StartOnboarding'));

const applyProtectedRoute = (children: ReactNode) => {
  return <ProtectedRoute>{children}</ProtectedRoute>;
};

const App = () => (
  <BrowserRouter>
    <Suspense fallback={<Loader />}>
      <Sentry.ErrorBoundary
        fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
      >
        <Routes>
          <Route path='/login' element={<SignIn />} />
          <Route path='/sign-up' element={<SignUp />} />
          <Route
            path='/subscription-success'
            element={<SubscriptionSuccess />}
          />
          <Route path='/subscription-failed' element={<SubscriptionFailed />} />
          <Route path='/reset-password/' element={<ResetPassword />} />
          <Route element={applyProtectedRoute(<MainLayout />)}>
            <Route element={<CabinetLayout />}>
              <Route path='/start' element={<StartOnboarding />} />
              <Route path='/brand-wiki-setup' element={<BrandWikiSetup />} />
              <Route path='/drafts-list' element={<DraftPosts />} />
              <Route path='/drafts/:postId?' element={<BasePost />} />
              <Route path='/clients' element={<Clients />} />
              <Route path='/brand-settings/*' element={<BrandSettings />} />
              <Route
                path='/drafts/formatted/:postId'
                element={<FormattedPosts />}
              />
              <Route path='/schedule' element={<Schedule />} />
              <Route path='/settings/*' element={<Settings />} />
              {/* V2 ROUTES */}
              <Route path='/brand-strategy' element={<BrandStrategy />} />
              <Route path='/post-ideas' element={<PostIdeas />} />
              {/* V2 ROUTES */}
              <Route path='/admin' element={<Admin />}>
                <Route path='users' element={<AdminUsers />} />
                <Route path='llm-logs' element={<AdminLlmLogs />} />
              </Route>
            </Route>
          </Route>

          <Route path='/' element={<Navigate to='/post-ideas' />} />
          <Route path='*' element={<NotFound />} />
        </Routes>
      </Sentry.ErrorBoundary>
    </Suspense>
  </BrowserRouter>
);

export default App;
